import * as React from "react";
import { CommandBar, ICommandBarItemProps, ICommandBarStyles, IContextualMenuItem, Stack } from "@fluentui/react";
import { useId } from "@fluentui/react-hooks";
import { buildTestId, ComponentTestIds } from "../../helpers/testIdHelper";
import { useResizeObserver } from "../../hooks/useResizeObserver";
import {
  buildConfirmDialogTestIds,
  ConfirmationDialogTestIds,
  ConfirmDialogType,
} from "../ConfirmDialog/ConfirmDialogTypes";
import { InternalConfirmDialog } from "../ConfirmDialog/InternalConfirmDialog";
import { InfoBlock, InfoBlockProps } from "../InfoBlock/InfoBlock";

export interface ActionBarProps {
  /**
   * List of actions to display in the action bar
   */
  actions: ActionType [] ;
  /**
   * Status info to show under the action bar
   */
  infoBlocks?: InfoBlockProps [];
  /**
   * If all the actions should be disabled simultaneously
   */
  disableAll?: boolean;
  /**
   * Triggered when the user clicks on any action.
   * Allows executing logic prior to invoking the action.
   */
  onActionClick?: (actionKey: string) => void;
  /**
   * Id used to get the action bar elements for unit tests
   */
  testId?: string;
}

export interface ConfirmAction extends Action {
  /**
   * Title of the confirmation dialog
   */
  title: string;
  /**
   * Description of the confirmation dialog
   */
  description: string;
  /**
   * Action to perform when clicking on YES in the confirmation
   */
  onConfirm: () => void;
  /**
   * Action to perform when clicking on NO in the confirmation
   */
  onCancel?: () => void;
  /**
   * Toggles confirmation bar's visibility and an action to perform when clicking on action
   */
  onClick?: () => void;
}

export interface CustomAction extends Action {
  /**
   * Action to perform when clicking on the action
   */
  onClick: () => void;
}

export interface MenuAction extends Action {
  /**
   * Specifies if the action is a submenu
   */
  subMenu: { items: ConfirmAction[] | CustomAction [] };
}

/**
 * An Action bar that supports custom actions, action menues and confirmation actions
 * A confirmation action will display a confimration dialog that asks the user to proceed or cancel the action
 */

export type ActionType = ConfirmAction | CustomAction | MenuAction;

export interface Action {
  /**
   * A unique key to identify the action
   */
  key: string;
  /**
   * Title of the action
   */
  text: string;
  /**
   * A custom icon to display on the action item along with the text
   */
  icon?: string;
  /**
   * If the action should be disabled
   */
  disabled?: boolean;
  /**
   * A function to edit how the icon is being rendered
   */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  onRenderIcon?: (iconProps?: any) => JSX.Element;
  /**
   * Id used to build test ids for sub components of the Action
   */
  testId?: string;
}

const actionBarStyles: ICommandBarStyles = {
  root: {
    height: "36px",
    padding: "0px",
    borderBottom: "1px solid rgb(214, 214, 214)",
    ".ms-Button-label": {
      fontSize: "13px",
      fontWeight: 400,
    },
    ".ms-Button-menuIcon": { lineHeight: "20px" },
  },
};
export interface ConfirmActionTestIds extends ComponentTestIds {
  confirmDialog: ConfirmationDialogTestIds;
}

export const buildConfirmActionTestIds = (testId?: string): ConfirmActionTestIds => ({
  component: buildTestId(testId),
  confirmDialog: buildConfirmDialogTestIds(buildTestId(testId, "-confirm-dialog")),
});

export const ActionBar = ({ actions, infoBlocks, disableAll, onActionClick, testId }: ActionBarProps): JSX.Element => {
  const targetId = `confirm-dialog-action-bar-${useId()}`;
  const ref = React.useRef(null);
  const confirmations: JSX.Element [] = [];
  const [confirmationState, setConfirmationState] = React.useState<{ [key: string]: boolean }>({});
  const lastActionCancel = React.useRef<(() => void) | undefined>(undefined);

  const isConfirmAction = (action: ActionType): boolean => "title" in action;
  const isMenuAction = (action: ActionType): boolean => "subMenu" in action;

  const setPreviousActionCancel = (action: ActionType): void => {
    lastActionCancel.current = (action as ConfirmAction).onCancel;
  };
  const triggerLastActionCancel = (): void => {
    lastActionCancel.current?.();
    lastActionCancel.current = undefined;
  };

  React.useEffect(() => {
    const newConfirmationState: { [key: string]: boolean } = {};
    actions.forEach((action, index) => {
      if (isConfirmAction(action)) {
        const actionId = `confimration${index}`;
        newConfirmationState[actionId] = false;
      }
    });
    setConfirmationState(newConfirmationState);
  }, [actions]);

  const getActionItems = (): ICommandBarItemProps [] => {
    const items: ICommandBarItemProps[] = [];

    actions.forEach((action: ActionType, index) => {
      const subMenu: IContextualMenuItem[] = [];
      const actionId = `confirmation${index}`;
      const toggleConfirmationVisible = (): void => {
        const newState = { [actionId]: !confirmationState[actionId] };
        setConfirmationState(newState);
      };
      const internalOnClick = (actionKey: string): void => {
        triggerLastActionCancel();
        onActionClick?.(actionKey);
        toggleConfirmationVisible();
        setPreviousActionCancel(action);
        (action as ConfirmAction | CustomAction).onClick?.();
      };

      if (isMenuAction(action)) {
        (action as MenuAction).subMenu.items.forEach(item => {
          subMenu.push({
            ...item,
            "data-test-id": item.testId,
            id: item.key,
            iconProps: item.icon ? { iconName: item.icon } : undefined,
          });
        });
      }

      if (isConfirmAction(action)) {
        const confirmAction = action as ConfirmAction;
        const confirmActionTestIds = buildConfirmActionTestIds(action.testId);
        const onConfirmAction = (): void => {
          toggleConfirmationVisible();
          confirmAction.onConfirm();
        };
        const onCancelAction = (): void => {
          toggleConfirmationVisible();
          triggerLastActionCancel();
        };
        confirmations.push(
          <div key={actionId}>
            {confirmationState && confirmationState[actionId] && (
              <InternalConfirmDialog
                testId={confirmActionTestIds.confirmDialog.component}
                targetId={targetId}
                title={confirmAction.title}
                description={confirmAction.description}
                onConfirm={onConfirmAction}
                onCancel={onCancelAction}
                onDismiss={onCancelAction}
                type={ConfirmDialogType.COMMAND_BAR}
                styles={{ dialogWidth }}
              />
            )}
          </div>,
        );
      }
      const newAction: ICommandBarItemProps = {
        ...action,
        onClick: () => {
          internalOnClick(action.key);
        },
        id: action.key,
        "data-test-id": action.testId,
        iconProps: action.icon ? { iconName: action.icon } : undefined,
        subMenuProps: subMenu.length ? { items: subMenu } : undefined,
        disabled: disableAll || action.disabled,
      };
      delete newAction.description;
      items.push(newAction);
    });
    return items;
  };

  const { width: dialogWidth } = useResizeObserver(ref);

  return (
    <Stack style={{ flex: "0 0 38px" }}>
      <div ref={ref}>
        <CommandBar
          data-test-id={testId}
          id={targetId}
          styles={actionBarStyles}
          items={getActionItems()}
        />
        {infoBlocks && (
          infoBlocks?.map((statusInfo, index) => (
            <InfoBlock
              {...statusInfo}
              // eslint-disable-next-line react/no-array-index-key
              key={`infoBlock${index}`}
            />
          ))
        )}
        {confirmations}
      </div>
    </Stack>
  );
};
